import {
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import ReactPlayer from "react-player/youtube";
import { AspectRatio } from "@chakra-ui/layout";
import { callWithAuth, postListenTime } from "@/api/Api";
import { PostListenTimeRequest } from "@shared_types";
import "../../App.css";
import { useUpgrade } from "../../context/UpgradeContext";
import { IoMdPlay } from "react-icons/io";

interface ActivityPlayerProps {
  youtubeId: string;
  onTimeUpdate: (time: number) => void;
  startTime: number;
  endTime: number;
  onPlay: () => void;
  onTrackListeningTime?: (time: number) => void | undefined;
}

export interface ActivityPlayerHandle {
  seekTo: (time: number) => void;
  pause: () => void;
  play: () => void;
  triggerOverlayClick: () => void;
}

const ActivityPlayer = forwardRef<ActivityPlayerHandle, ActivityPlayerProps>(
  (
    {
      youtubeId,
      onTrackListeningTime,
      onTimeUpdate,
      startTime,
      endTime,
      onPlay,
    },
    ref
  ) => {
    const { isUpgrading } = useUpgrade();
    const youtubePrefix = "youtube.com/watch?v=";
    const url = youtubePrefix + youtubeId;
    const playerRef = useRef<ReactPlayer>(null);
    const [playing, setPlaying] = useState(false);

    useImperativeHandle(ref, () => ({
      seekTo(time) {
        playerRef.current?.seekTo(time / 1000, "seconds");
      },
      pause() {
        handlePause();
      },
      play() {
        handlePlay();
      },
      triggerOverlayClick() {
        // Try to click the actual play button DOM element for better iOS Safari compatibility
        const playButton = document.querySelector('.absolute.rounded-full.z-20') as HTMLElement;
        if (playButton) {
          playButton.click();
        } else {
          // Try clicking the ReactPlayer iframe directly
          const iframe = document.querySelector('iframe[src*="youtube"]') as HTMLElement;
          if (iframe) {
            iframe.click();
          } else {
            // Fallback to the handler method
            handleOverlayClick();
          }
        }
      },
    }));

    const handlePause = () => {
      if (playing !== false) {
        setPlaying(false);
      }
    };

    const handlePlay = () => {
      if (playing !== true) {
        setPlaying(true);
        onPlay();
      }
    };

    // tracks the watch time every X seconds
    // no big deal if this requests fails for whatever reason
    const trackWatchTime = (startTimeMs: number) => {
      if (!onTrackListeningTime) return;

      let lastMeasuredTimeSeconds = startTimeMs / 1000;
      const intervalTimeSeconds = 2;
      const trackWatchTime = setInterval(() => {
        try {
          // kill captions from the youtube video
          // this is naughty
          (playerRef.current as any).player.player.player.unloadModule("cc");
          (playerRef.current as any).player.player.player.unloadModule(
            "captions"
          );
        } catch (err) {
          console.warn(err);
        }
        if (playerRef.current && playerRef.current.getCurrentTime) {
          const currentTimeSeconds = playerRef.current.getCurrentTime();
          // realistic amount of watch time passed, post it
          // gives us space for a valid time add on the high end
          const timeBuffer = 2;
          const timeDiff = currentTimeSeconds - lastMeasuredTimeSeconds;
          if (0 < timeDiff && timeDiff <= intervalTimeSeconds + timeBuffer) {
            const timeDiffFloored = Math.floor(timeDiff);
            onTrackListeningTime(timeDiffFloored);
            const data: PostListenTimeRequest = { listenTime: timeDiffFloored };
            callWithAuth(postListenTime, data).catch((err) => {
              console.error(err);
            });
          }
          lastMeasuredTimeSeconds = currentTimeSeconds;
        }
      }, intervalTimeSeconds * 1000);
      return trackWatchTime;
    };

    useEffect(() => {
      const interval = setInterval(() => {
        if (playerRef.current && playerRef.current.getCurrentTime) {
          const currentTime = playerRef.current.getCurrentTime() * 1000;
          if (currentTime >= endTime || currentTime < startTime) {
            playerRef.current.seekTo(startTime / 1000, "seconds");
          }
          onTimeUpdate(currentTime);
        }
      }, 10);
      return () => {
        clearInterval(interval);
      };
    }, [startTime, endTime, onTimeUpdate, playing]);

    // tracking the watch time
    useEffect(() => {
      const watchTimeInterval = trackWatchTime(startTime);
      return () => {
        clearInterval(watchTimeInterval);
      };
    }, [startTime, endTime]);

    // Add effect to pause video when upgrading
    useEffect(() => {
      if (isUpgrading) {
        handlePause();
      }
    }, [isUpgrading, handlePause]);
    
    const handleOverlayClick = () => {
      if (playing !== true) {
        setPlaying(true);
        onPlay();
      } else {
        setPlaying(false);
      }
    };

    return (
      <div className="relative justify-center w-full">
        <AspectRatio
          ratio={16 / 9}
          className="p-0"
          minHeight="200px"
        >
          <div className="relative">
            <ReactPlayer
              ref={playerRef}
              url={url + `?start=${startTime / 1000}`}
              playing={playing}
              onPlay={handlePlay}
              onPause={handlePause}
              height="100%"
              width="100%"
            />
            {!playing && (
              <div
                onClick={handleOverlayClick}
                className={`absolute rounded-full z-20 bg-[#FAFAFA] bg-opacity-80 flex items-center justify-center w-16 h-16 -translate-x-1/2 -translate-y-1/2 left-1/2 top-1/2`}
              >
                <div className="absolute z-5 left-[53%] top-1/2 -translate-x-1/2 -translate-y-1/2">
                  <IoMdPlay color="black" size="2em" />
                </div>
              </div>
            )}
          </div>
        </AspectRatio>
        {!playing && (
          <div
            id="overlay-paused"
            onClick={handleOverlayClick}
            className="absolute inset-0 z-0 bg-black bg-opacity-20 flex items-center justify-center pointer-events-none"
          ></div>
        )}
      </div>
    );
  }
);

export default ActivityPlayer;